/**
 * The builder half of the practice deep-link contract (pedagogy spec §4.3.9),
 * shared by the client (Progress, the exam wrapper, Today) and the server (the
 * Today extras of step 2.8c, which carry a ready `href`). One builder, so the
 * link a student gets from the result page and the one Today serves for the
 * same suggestion cannot drift apart. The reader that fills the setup form in
 * lives next to the page (`client/src/features/practice/deepLink.ts`).
 *
 * | Param               | Meaning on `/practice`                                                       |
 * | ------------------- | ---------------------------------------------------------------------------- |
 * | `chapters`, `topics`| preselect; topic mode unless `mode=mixed` comes with chapters                |
 * | `types=numeric,open`| preselects the question types                                                |
 * | `count=4`           | preselects the count (clamped to 1-30)                                       |
 * | `primary=1`         | topic mode, and the batch holds to the chosen topics' own questions          |
 * | `mode=mixed`        | keeps mixed mode for a link that also carries `chapters`                     |
 * | `set=…`             | an exam-wrapper set that starts itself (step 2.8c): `which-principle`, `timed-pair` |
 * | `perPoint=13.33`    | `timed-pair`: minutes per exam point of the timed pair's pace                |
 * | `from=<id>`         | provenance: the exam session the set was suggested from                      |
 */
import type { QuestionType } from "./types.js";

/** What `GET /api/practice/questions?limit=` accepts. */
export const MIN_COUNT = 1;
export const MAX_COUNT = 30;
export const DEFAULT_COUNT = 10;

/** The size of a short, focused batch: Progress's "Öva på dessa" and the exam wrapper's "Starta". */
export const FOCUSED_COUNT = 4;

/** The exam wrapper's mixed sets (§7.3.5, §7.3.6). */
export const PRACTICE_SETS = ["which-principle", "timed-pair"] as const;
export type PracticeSet = (typeof PRACTICE_SETS)[number];

export type LinkMode = "topic" | "mixed";

/** Clamps a count into what the route accepts; a non-integer reads as the default. */
export function clampCount(raw: string | number): number {
  const parsed = typeof raw === "number" ? raw : Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return DEFAULT_COUNT;
  return Math.min(MAX_COUNT, Math.max(MIN_COUNT, Math.trunc(parsed)));
}

export interface PracticeLink {
  mode?: LinkMode;
  chapters?: readonly number[];
  topics?: readonly string[];
  types?: readonly QuestionType[];
  count?: number;
  /** `primary=1`. */
  primary?: boolean;
  set?: PracticeSet;
  /** Minutes per exam point (`timed-pair` only), written with at most two decimals. */
  perPoint?: number;
  /** The exam session the set comes from (stored as `origin.examSessionId`). */
  from?: string;
}

/** `/practice?…` for a link, params in the contract's order; no params gives `/practice`. */
export function practiceDeepLink(link: PracticeLink): string {
  const parts: string[] = [];
  if (link.mode) parts.push(`mode=${link.mode}`);
  if (link.set) parts.push(`set=${link.set}`);
  if (link.chapters && link.chapters.length > 0) parts.push(`chapters=${link.chapters.join(",")}`);
  if (link.topics && link.topics.length > 0) {
    parts.push(`topics=${link.topics.map(encodeURIComponent).join(",")}`);
  }
  if (link.types && link.types.length > 0) parts.push(`types=${link.types.join(",")}`);
  if (link.count !== undefined) parts.push(`count=${clampCount(link.count)}`);
  if (link.primary) parts.push("primary=1");
  if (link.perPoint !== undefined && Number.isFinite(link.perPoint) && link.perPoint > 0) {
    parts.push(`perPoint=${Number(link.perPoint.toFixed(2))}`);
  }
  if (link.from) parts.push(`from=${encodeURIComponent(link.from)}`);
  return parts.length > 0 ? `/practice?${parts.join("&")}` : "/practice";
}
